import { useCallback, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { isAfter, parseISO, startOfDay } from "date-fns";
import { useIsMutating } from "@tanstack/react-query";
import {
    ActionButton,
    Button,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    FormCheckbox,
    Input,
    RadioInput,
    Select,
    TextArea,
} from "@jhool-io/fe-components";
import {
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";
import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import {
    IEditIntakeNoteMedPayload,
    IGetClientNotes,
    ISessionNote,
    Modality,
    SessionLocations,
} from "../../../../modules/notes/types/notes.types";
import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";
import { INTAKE_NOTE_MED_LABELS } from "../../../../utils/constants";
import { useFetchClientNotesList } from "../../../../hooks/queries/note";
import ViewFile from "../../../../shared-ui/ViewFile/ViewFile";
import DiagnosisCodeSelect from "../../../../modules/notes/components/DiagnosisCodeSelect/DiagnosisCodeSelect";

import * as FilePicker from "../../../../shared-ui/FilePicker/FilePicker";
import * as Progress from "../../../../shared-ui/Progress/Progress";
import DeleteIcon from "../../../../icons/Delete";
import { IViewFile } from "../../../../utils/types";
import {
    ClientEducation,
    ClientGender,
    ClientRace,
    ClientRelationship,
    ClientReligion,
    ClientSexualOrientation,
    Pronouns,
    UserSex,
} from "../../../../utils/types/client";
import MedNoteEPrescribe from "../../../../modules/e-prescirbe/components/MedNoteEPrescribe/MedNoteEPrescribe";
import ScreeningTools from "../../../../modules/notes/components/ScreeningTools/ScreeningTools";

interface EditIntakeNoteMedProps {
    noteDetails: ISessionNote;
}

type Option = {
    label: React.ReactNode;
    value: string;
};

const COMMUNICATION_FACTORS_OPTIONS = [
    "Need to manage maladaptive communication (e.g., related to high anxiety, high reactivity, repeated questions, or disagreement) among participants",
    "Caregiver emotions or behaviors interfered with the caregiver's understanding and ability to assist in the implementation of the treatment plan",
    "Evidence or disclosure of a sentinel event and mandated reporting to a third party",
    "Use of play equipment or other physical devices to communicate with the client to overcome barriers to therapeutic or diagnostic interaction",
    "Client has not developed, or lost, either the expressive language communication skills to explain their symptoms and respond to treatment",
    "Client lacks the receptive communication skills to understand the therapist when using typical language for communication",
];

const FREQUENCY_OF_SESSIONS_OPTIONS = [
    "Weekly",
    "Bi-Weekly",
    "Monthly",
    "Other",
];

const PATIENT_ABILITY_OPTIONS = [
    "Consistent, good-quality patient-oriented evidence",
    "Diminished/poor or limited-quality patient-oriented evidence",
];

const IDEATION_OPTIONS = ["Denies", "Yes"];

const REPORTED_OPTIONS = ["Reported", "Not reported"];

const BOOLEAN_OPTIONS = [
    { label: "Yes", value: true },
    { label: "No", value: false },
];

const RISK_ASSESSMENT_FIELDS = [
    { name: "current_suicidal_ideation", options: IDEATION_OPTIONS },
    { name: "current_homicidal_ideation", options: IDEATION_OPTIONS },
    { name: "reported_violent_behavior", options: IDEATION_OPTIONS },
    { name: "inappropriate_illegal_behaviors", options: IDEATION_OPTIONS },
    { name: "history_of_violence_to_self", options: REPORTED_OPTIONS },
    { name: "history_of_violence_to_others", options: REPORTED_OPTIONS },
    { name: "auditory_hallucinations", options: REPORTED_OPTIONS },
    { name: "visual_hallucinations", options: REPORTED_OPTIONS },
] as const;

export default function EditIntakeNoteMed({
    noteDetails,
}: EditIntakeNoteMedProps) {
    const [showPrompt, setShowPrompt] = useState(false);
    const [selectedNote, setSelectedNote] = useState({} as IGetClientNotes);
    const [docInView, setDocInView] = useState<IViewFile | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);
    const [localDateOfNextSession, setLocalDateOfNextSession] =
        useState<Date | null>(
            noteDetails.note_content?.date_of_next_session
                ? parseISO(noteDetails.note_content.date_of_next_session)
                : null
        );

    const clientNoteParams = {
        type: noteDetails?.type as string,
        load_previous_notes: true,
        provider_id: noteDetails?.provider?.provider_id as string,
    };

    const isSigning = useRef(false);

    const { data: notes } = useFetchClientNotesList(
        noteDetails?.client?.client_id as string,
        clientNoteParams
    );

    const {
        handleAutoSaveNote,
        handleEditDraftNote,
        handleSignNote,
        handleAddNoteDocument,
        handleDeleteNoteContentDocument,
        uploadProgress,
    } = useEditNote();

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        watch,
        control,
        trigger,
        formState: { errors, submitCount },
    } = useForm<IEditIntakeNoteMedPayload>({
        resolver: yupResolver(
            yup.object({
                diagnosis_codes: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .when([], {
                        is: () => isSigning.current,
                        then: yup
                            .array()
                            .min(1, "Primary diagnosis is required"),
                        otherwise: yup.array(),
                    }),
                secondary_diagnosis: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .nullable(),
                tertiary_diagnosis: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .nullable(),
                additional_diagnosis_one: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .nullable(),
                additional_diagnosis_two: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .nullable(),
                preferred_name: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                pronoun: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                sex: yup.string(),
                gender_identity: yup.string(),
                sexual_orientation: yup.string(),
                monogamous_polyamorous: yup.string(),
                religion: yup.string(),
                religion_details: yup.string(),
                race: yup.string(),
                ethnicity: yup.string(),
                education: yup.string(),
                current_employment: yup.string(),
                relationship: yup.string(),
                relationship_details: yup.string(),
                emergency_contact_information: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                names_and_relations_of_others_present_in_the_interview:
                    yup.string(),
                chief_complaint: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                history_of_present_illness: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                past_psychiatric_history: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                past_medical_history: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                past_psychiatric_hospitalization: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                current_medications: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                allergies: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                social_history: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                family_history: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                trauma_history: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                lifestyle_excercise_diet_sleep: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                appearance: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                movement: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                speech_orientation: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                affect_mood: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                attention_concentration_cognition: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                overall_presentation: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                patient_ability_and_capability_to_respond_to_treatment: yup
                    .array()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup
                            .array()
                            .min(1, "Please select at least one option")
                            .required("Please select at least one option"),
                        otherwise: yup.array().nullable(),
                    }),
                diminished_poor_or_limited_quality_patient_oriented_evidence_details:
                    yup
                        .string()
                        .when(
                            "patient_ability_and_capability_to_respond_to_treatment",
                            (ability: string[], field) =>
                                isSigning.current &&
                                ability?.includes(PATIENT_ABILITY_OPTIONS[1])
                                    ? field
                                          .trim()
                                          .required("This field is required")
                                    : field
                        ),
                ros: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                current_suicidal_ideation: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .nullable()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string().nullable(),
                }),
                current_homicidal_ideation: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .nullable()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string().nullable(),
                }),
                reported_violent_behavior: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .nullable()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string().nullable(),
                }),
                inappropriate_illegal_behaviors: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .nullable()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string().nullable(),
                }),
                history_of_violence_to_self: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .nullable()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string().nullable(),
                }),
                history_of_violence_to_others: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .nullable()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string().nullable(),
                }),
                auditory_hallucinations: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .nullable()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string().nullable(),
                }),
                visual_hallucinations: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .nullable()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string().nullable(),
                }),
                other_pertinent_information: yup.string(),
                lab_test_data_review: yup.string(),
                psychiatric_screening_measure_results: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                note: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                plan: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                frequency_of_sessions: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1, "Frequency of sessions is required")
                        .required("Frequency of sessions is required"),
                    otherwise: yup.array().nullable(),
                }),
                other_frequency_of_sessions: yup.string(),
                date_of_next_session: yup.date().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .date()
                        .required("Date of next session is required")
                        .test(
                            "is-after-dos",
                            "Date of next session must be after the current session date",
                            function validateDateAfterDOS(value) {
                                if (!value) return false;
                                if (!noteDetails.date_of_service) return true;
                                const dos = startOfDay(
                                    parseISO(noteDetails.date_of_service)
                                );
                                const nextSession = startOfDay(value);
                                return isAfter(nextSession, dos);
                            }
                        ),
                    otherwise: yup.date().nullable(),
                }),
                prognosis: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                education_including_health_promotion_maintenance_and_psychosocial_needs:
                    yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .trim()
                            .required("This field is required"),
                        otherwise: yup.string(),
                    }),
                lab_work_ordered: yup.string(),
                referrals_and_recommendations: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                overall_goals_of_care_frequency_and_duration_of_visits: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .trim()
                            .required("This field is required"),
                        otherwise: yup.string(),
                    }),
                treatment_goal_one: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                treatment_goal_two: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                treatment_goal_three: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                location_of_the_client: yup
                    .string()
                    .nullable()
                    .when("session_location", (sessionLocation, field) =>
                        sessionLocation === SessionLocations.Telehealth &&
                        isSigning.current
                            ? field.required("Client location is required")
                            : field
                    ),
                other_location_of_the_client: yup
                    .string()
                    .nullable()
                    .when(["session_location", "location_of_the_client"], {
                        is: (sessionLocation?: string, location?: string) =>
                            sessionLocation === SessionLocations.Telehealth &&
                            location === "other" &&
                            isSigning.current,
                        then: yup
                            .string()
                            .nullable()
                            .trim()
                            .required("Other client location is required"),
                        otherwise: yup.string().nullable(),
                    }),
                location_of_the_provider: yup
                    .string()
                    .nullable()
                    .when("session_location", (sessionLocation, field) =>
                        sessionLocation === SessionLocations.Telehealth &&
                        isSigning.current
                            ? field.required("Provider location is required")
                            : field
                    ),
                other_location_of_the_provider: yup
                    .string()
                    .nullable()
                    .when(["session_location", "location_of_the_provider"], {
                        is: (sessionLocation?: string, location?: string) =>
                            sessionLocation === SessionLocations.Telehealth &&
                            location === "other" &&
                            isSigning.current,
                        then: yup
                            .string()
                            .nullable()
                            .trim()
                            .required("Other provider location is required"),
                        otherwise: yup.string().nullable(),
                    }),
                consent_was_received_for_the_teletherapy_session: yup.bool(),
                the_session_was_conducted_via_video: yup.bool(),
                session_location: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .nullable()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                modality: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                communication_factors_impacting_treatment: yup
                    .array()
                    .nullable(),
                details_around_communication_factors_impacting_treatment:
                    yup.string(),
                signature: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().trim().required("Signature is required"),
                    otherwise: yup.string(),
                }),
            })
        ),
        mode: "onChange",
        defaultValues: noteDetails.note_content
            ? {
                  ...noteDetails.note_content,
                  patient_ability_and_capability_to_respond_to_treatment:
                      noteDetails.note_content
                          .patient_ability_and_capability_to_respond_to_treatment ??
                      [],
                  secondary_diagnosis: noteDetails?.note_content
                      ?.secondary_diagnosis
                      ? [noteDetails?.note_content?.secondary_diagnosis]
                      : undefined,
                  tertiary_diagnosis: noteDetails?.note_content
                      ?.tertiary_diagnosis
                      ? [noteDetails?.note_content?.tertiary_diagnosis]
                      : undefined,
                  additional_diagnosis_one: noteDetails?.note_content
                      ?.additional_diagnosis_one
                      ? [noteDetails?.note_content?.additional_diagnosis_one]
                      : undefined,
                  additional_diagnosis_two: noteDetails?.note_content
                      ?.additional_diagnosis_two
                      ? [noteDetails?.note_content?.additional_diagnosis_two]
                      : undefined,
              }
            : { patient_ability_and_capability_to_respond_to_treatment: [] },
        shouldFocusError: false,
    });

    const watchedValues = watch();
    const requiresPatientAbilityDetails =
        watch(
            "patient_ability_and_capability_to_respond_to_treatment"
        )?.includes(PATIENT_ABILITY_OPTIONS[1]) ?? false;

    const isAddDocMutating = useIsMutating(["add-note-content-document"]);
    const isDeleteDocMutating = useIsMutating(["delete-note-content-document"]);

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (selectedFiles && selectedFiles.length > 0) {
                setSelectedFiles([...acceptedFiles, ...selectedFiles]);
            } else {
                setSelectedFiles([...acceptedFiles]);
            }
        },
        [selectedFiles]
    );

    const handleRemoveSelectedFile = (filename: string) => {
        if (selectedFiles) {
            const filesArr = [...selectedFiles].filter(
                (file) => file.name !== filename
            );
            if (filesArr.length === 0) setSelectedFiles(null);
            else setSelectedFiles(filesArr);
        }
    };

    const handleLoadPreviousSession = (note: IGetClientNotes) => {
        setValue(
            "location_of_the_client",
            note.note_content.location_of_the_client
        );
        setValue(
            "other_location_of_the_client",
            note.note_content.other_location_of_the_client
        );
        setValue(
            "location_of_the_provider",
            note.note_content.location_of_the_provider
        );
        setValue(
            "other_location_of_the_provider",
            note.note_content.other_location_of_the_provider
        );
        setValue(
            "consent_was_received_for_the_teletherapy_session",
            note.note_content.consent_was_received_for_the_teletherapy_session
        );
        setValue(
            "the_session_was_conducted_via_video",
            note.note_content.the_session_was_conducted_via_video
        );

        setValue("diagnosis_codes", note.note_content.diagnosis_codes);
        setValue(
            "secondary_diagnosis",
            note.note_content.secondary_diagnosis
                ? [note.note_content.secondary_diagnosis]
                : undefined
        );
        setValue(
            "tertiary_diagnosis",
            note.note_content.tertiary_diagnosis
                ? [note.note_content.tertiary_diagnosis]
                : undefined
        );
        setValue(
            "additional_diagnosis_one",
            note.note_content.additional_diagnosis_one
                ? [note.note_content.additional_diagnosis_one]
                : undefined
        );
        setValue(
            "additional_diagnosis_two",
            note.note_content.additional_diagnosis_two
                ? [note.note_content.additional_diagnosis_two]
                : undefined
        );
        setValue("preferred_name", note.note_content.preferred_name);
        setValue("pronoun", note.note_content.pronoun);
        setValue("sex", note.note_content.sex);
        setValue("gender_identity", note.note_content.gender_identity);
        setValue("sexual_orientation", note.note_content.sexual_orientation);
        setValue(
            "monogamous_polyamorous",
            note.note_content.monogamous_polyamorous
        );
        setValue("religion", note.note_content.religion);
        setValue("religion_details", note.note_content.religion_details);
        setValue("race", note.note_content.race);
        setValue("ethnicity", note.note_content.ethnicity);
        setValue("education", note.note_content.education);
        setValue("current_employment", note.note_content.current_employment);
        setValue("relationship", note.note_content.relationship);
        setValue(
            "relationship_details",
            note.note_content.relationship_details
        );
        setValue(
            "emergency_contact_information",
            note.note_content.emergency_contact_information
        );
        setValue(
            "names_and_relations_of_others_present_in_the_interview",
            note.note_content
                .names_and_relations_of_others_present_in_the_interview
        );
        setValue("chief_complaint", note.note_content.chief_complaint);
        setValue(
            "history_of_present_illness",
            note.note_content.history_of_present_illness
        );
        setValue(
            "past_psychiatric_history",
            note.note_content.past_psychiatric_history
        );
        setValue(
            "past_medical_history",
            note.note_content.past_medical_history
        );
        setValue(
            "past_psychiatric_hospitalization",
            note.note_content.past_psychiatric_hospitalization
        );
        setValue("current_medications", note.note_content.current_medications);
        setValue("allergies", note.note_content.allergies);
        setValue("social_history", note.note_content.social_history);
        setValue("family_history", note.note_content.family_history);
        setValue("trauma_history", note.note_content.trauma_history);
        setValue(
            "lifestyle_excercise_diet_sleep",
            note.note_content.lifestyle_excercise_diet_sleep
        );
        setValue("appearance", note.note_content.appearance);
        setValue("movement", note.note_content.movement);
        setValue("speech_orientation", note.note_content.speech_orientation);
        setValue("affect_mood", note.note_content.affect_mood);
        setValue(
            "attention_concentration_cognition",
            note.note_content.attention_concentration_cognition
        );
        setValue(
            "overall_presentation",
            note.note_content.overall_presentation
        );
        setValue(
            "patient_ability_and_capability_to_respond_to_treatment",
            note.note_content
                .patient_ability_and_capability_to_respond_to_treatment
        );
        setValue(
            "diminished_poor_or_limited_quality_patient_oriented_evidence_details",
            note.note_content
                .diminished_poor_or_limited_quality_patient_oriented_evidence_details
        );
        setValue("ros", note.note_content.ros);
        setValue(
            "current_suicidal_ideation",
            note.note_content.current_suicidal_ideation
        );
        setValue(
            "current_homicidal_ideation",
            note.note_content.current_homicidal_ideation
        );
        setValue(
            "reported_violent_behavior",
            note.note_content.reported_violent_behavior
        );
        setValue(
            "inappropriate_illegal_behaviors",
            note.note_content.inappropriate_illegal_behaviors
        );
        setValue(
            "history_of_violence_to_self",
            note.note_content.history_of_violence_to_self
        );
        setValue(
            "history_of_violence_to_others",
            note.note_content.history_of_violence_to_others
        );
        setValue(
            "auditory_hallucinations",
            note.note_content.auditory_hallucinations
        );
        setValue(
            "visual_hallucinations",
            note.note_content.visual_hallucinations
        );
        setValue(
            "other_pertinent_information",
            note.note_content.other_pertinent_information
        );
        setValue(
            "lab_test_data_review",
            note.note_content.lab_test_data_review
        );
        setValue(
            "psychiatric_screening_measure_results",
            note.note_content.psychiatric_screening_measure_results
        );
        setValue("note", note.note_content.note);
        setValue("plan", note.note_content.plan);
        setValue(
            "frequency_of_sessions",
            note.note_content.frequency_of_sessions
        );
        setValue(
            "other_frequency_of_sessions",
            note.note_content.other_frequency_of_sessions
        );
        setValue(
            "date_of_next_session",
            note.note_content.date_of_next_session
        );
        setLocalDateOfNextSession(
            note.note_content.date_of_next_session
                ? parseISO(note.note_content.date_of_next_session)
                : null
        );
        setValue("prognosis", note.note_content.prognosis);
        setValue(
            "education_including_health_promotion_maintenance_and_psychosocial_needs",
            note.note_content
                .education_including_health_promotion_maintenance_and_psychosocial_needs
        );
        setValue("lab_work_ordered", note.note_content.lab_work_ordered);
        setValue(
            "referrals_and_recommendations",
            note.note_content.referrals_and_recommendations
        );
        setValue(
            "overall_goals_of_care_frequency_and_duration_of_visits",
            note.note_content
                .overall_goals_of_care_frequency_and_duration_of_visits
        );
        setValue("treatment_goal_one", note.note_content.treatment_goal_one);
        setValue("treatment_goal_two", note.note_content.treatment_goal_two);
        setValue(
            "treatment_goal_three",
            note.note_content.treatment_goal_three
        );
        setValue("session_location", note.note_content.session_location);
        setValue("modality", note.note_content.modality);
        setValue(
            "communication_factors_impacting_treatment",
            note.note_content.communication_factors_impacting_treatment
        );
        setValue(
            "details_around_communication_factors_impacting_treatment",
            note.note_content
                .details_around_communication_factors_impacting_treatment
        );
        setShowPrompt(false);
    };

    const handleIsChecked = (checkboxValue: string, values?: string[]) => {
        return Boolean(
            values?.find(
                (value: string) => value.trim() === checkboxValue.trim()
            )
        );
    };

    const pronounOptionsForSelect = Object.values(Pronouns).map((option) => ({
        label: removeEnumUnderscore(makeStringFirstLetterCapital(option)),
        value: option,
    }));

    const sexOptionsForSelect = Object.values(UserSex).map((option) => ({
        label: makeStringFirstLetterCapital(option),
        value: option,
    }));

    const genderIdentityOptionsForSelect = Object.values(ClientGender).map(
        (option) => ({
            label: removeEnumUnderscore(makeStringFirstLetterCapital(option)),
            value: option,
        })
    );

    const sexualOrientationOptionsForSelect = Object.values(
        ClientSexualOrientation
    ).map((option) => ({
        label: removeEnumUnderscore(makeStringFirstLetterCapital(option)),
        value: option,
    }));

    const religionOptionsForSelect = Object.values(ClientReligion).map(
        (option) => ({
            label: removeEnumUnderscore(makeStringFirstLetterCapital(option)),
            value: option,
        })
    );

    const raceOptionsForSelect = Object.values(ClientRace).map((option) => ({
        label: removeEnumUnderscore(makeStringFirstLetterCapital(option)),
        value: option,
    }));

    const educationOptionsForSelect = Object.values(ClientEducation).map(
        (option) => ({
            label: removeEnumUnderscore(makeStringFirstLetterCapital(option)),
            value: option,
        })
    );

    const relationshipOptionsForSelect = Object.values(ClientRelationship).map(
        (option) => ({
            label: removeEnumUnderscore(makeStringFirstLetterCapital(option)),
            value: option,
        })
    );

    const modalityOptionsForSelect = Object.keys(Modality).map((modality) => ({
        label: removeEnumUnderscore(modality),
        value: Modality[modality as keyof typeof Modality],
    }));

    const sessionLocationOptionsForSelect = Object.keys(SessionLocations).map(
        (location) => ({
            label: removeEnumUnderscore(location),
            value: SessionLocations[location as keyof typeof SessionLocations],
        })
    );

    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: handleAutoSaveNote,
        isSigningRef: isSigning,
    });

    return (
        <>
            {docInView ? (
                <Dialog open={Boolean(docInView)}>
                    <DialogContent
                        title={
                            docInView?.document_name ??
                            "Note content attachment"
                        }
                        handleCloseDialog={() => {
                            setDocInView(null);
                        }}
                        showFooter={false}
                    >
                        <ViewFile
                            url={docInView.url}
                            contentType={
                                docInView?.metadata?.content_type ?? ""
                            }
                            title={
                                docInView?.document_name ??
                                "Note content attachment"
                            }
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() =>
                    handleLoadPreviousSession(selectedNote)
                }
            />

            {notes && notes.data.length > 0 && (
                <div className="flex justify-end mb-[--spacing-header]">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                Load previous session note
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {notes &&
                                notes?.data?.map((note) => (
                                    <DropdownItem
                                        key={note.note_id}
                                        onClick={() => {
                                            setShowPrompt(true);
                                            setSelectedNote(note);
                                        }}
                                    >
                                        <div>
                                            {makeStringFirstLetterCapital(
                                                removeEnumUnderscore(
                                                    note.note_type
                                                )
                                            )}{" "}
                                            - Note content Details -{" "}
                                            {formatDate(note.date_of_service)}
                                        </div>
                                    </DropdownItem>
                                ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
            )}

            <form
                onSubmit={handleSubmit(handleSignNote)}
                aria-label="intake note form"
                id="edit-note-form"
            >
                <div className="mb-[--spacing-header]">
                    <NoteFormInfoBox />
                </div>
                <div className="fg">
                    <Controller
                        name="session_location"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isRequired
                                label={INTAKE_NOTE_MED_LABELS.session_location}
                                placeholder=""
                                options={sessionLocationOptionsForSelect}
                                onChange={(val) =>
                                    field.onChange(
                                        (val as { value: string }).value
                                    )
                                }
                                value={
                                    sessionLocationOptionsForSelect.find(
                                        (option) => option.value === field.value
                                    ) || null
                                }
                                hasError={!!errors.session_location}
                                errorText={errors.session_location?.message}
                            />
                        )}
                    />
                </div>
                {watch("session_location") === SessionLocations.Telehealth && (
                    <>
                        <h3 className="note-label-req">
                            {INTAKE_NOTE_MED_LABELS.location_of_the_client}
                        </h3>
                        <div className="fg flex gap-x-5">
                            {["home", "office", "other"].map((value) => (
                                <RadioInput
                                    key={value}
                                    {...register("location_of_the_client")}
                                    label={makeStringFirstLetterCapital(value)}
                                    value={value}
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.location_of_the_client === value
                                    }
                                />
                            ))}
                        </div>
                        {errors.location_of_the_client ? (
                            <div className="note-error">
                                <p>{errors.location_of_the_client?.message}</p>
                            </div>
                        ) : null}
                        {watch("location_of_the_client") === "other" && (
                            <div className="fg">
                                <Input
                                    {...register(
                                        "other_location_of_the_client"
                                    )}
                                    label={
                                        INTAKE_NOTE_MED_LABELS.other_location_of_the_client
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_location_of_the_client
                                    }
                                    isRequired
                                    hasError={
                                        !!errors.other_location_of_the_client
                                    }
                                    errorText={
                                        errors.other_location_of_the_client
                                            ?.message
                                    }
                                />
                            </div>
                        )}
                        <h3 className="note-label-req">
                            {INTAKE_NOTE_MED_LABELS.location_of_the_provider}
                        </h3>
                        <div className="fg flex gap-x-5">
                            {["home", "office", "other"].map((value) => (
                                <RadioInput
                                    key={value}
                                    {...register("location_of_the_provider")}
                                    label={makeStringFirstLetterCapital(value)}
                                    value={value}
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.location_of_the_provider === value
                                    }
                                />
                            ))}
                        </div>
                        {errors.location_of_the_provider ? (
                            <div className="note-error">
                                <p>
                                    {errors.location_of_the_provider?.message}
                                </p>
                            </div>
                        ) : null}
                        {watch("location_of_the_provider") === "other" && (
                            <div className="fg">
                                <Input
                                    {...register(
                                        "other_location_of_the_provider"
                                    )}
                                    label={
                                        INTAKE_NOTE_MED_LABELS.other_location_of_the_provider
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_location_of_the_provider
                                    }
                                    isRequired
                                    hasError={
                                        !!errors.other_location_of_the_provider
                                    }
                                    errorText={
                                        errors.other_location_of_the_provider
                                            ?.message
                                    }
                                />
                            </div>
                        )}
                        <div className="fg">
                            <Controller
                                name="consent_was_received_for_the_teletherapy_session"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        isRequired
                                        label={
                                            INTAKE_NOTE_MED_LABELS.consent_was_received_for_the_teletherapy_session
                                        }
                                        placeholder=""
                                        options={BOOLEAN_OPTIONS}
                                        onChange={(option) =>
                                            field.onChange(
                                                (option as { value: boolean })
                                                    .value
                                            )
                                        }
                                        value={
                                            BOOLEAN_OPTIONS.find(
                                                (option) =>
                                                    option.value === field.value
                                            ) ?? null
                                        }
                                        hasError={
                                            !!errors.consent_was_received_for_the_teletherapy_session
                                        }
                                        errorText={
                                            errors
                                                .consent_was_received_for_the_teletherapy_session
                                                ?.message
                                        }
                                    />
                                )}
                            />
                        </div>
                        <div className="fg">
                            <Controller
                                name="the_session_was_conducted_via_video"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        isRequired
                                        label={
                                            INTAKE_NOTE_MED_LABELS.the_session_was_conducted_via_video
                                        }
                                        placeholder=""
                                        options={BOOLEAN_OPTIONS}
                                        onChange={(option) =>
                                            field.onChange(
                                                (option as { value: boolean })
                                                    .value
                                            )
                                        }
                                        value={
                                            BOOLEAN_OPTIONS.find(
                                                (option) =>
                                                    option.value === field.value
                                            ) ?? null
                                        }
                                        hasError={
                                            !!errors.the_session_was_conducted_via_video
                                        }
                                        errorText={
                                            errors
                                                .the_session_was_conducted_via_video
                                                ?.message
                                        }
                                    />
                                )}
                            />
                        </div>
                    </>
                )}

                <div className="fg">
                    <Controller
                        name="diagnosis_codes"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={INTAKE_NOTE_MED_LABELS.diagnosis_codes}
                                value={field.value}
                                onChange={field.onChange}
                                isRequired
                                errorText={
                                    errors.diagnosis_codes?.type === "typeError"
                                        ? "Diagnosis codes are required"
                                        : errors.diagnosis_codes?.message
                                }
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="secondary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    INTAKE_NOTE_MED_LABELS.secondary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.secondary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="tertiary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    INTAKE_NOTE_MED_LABELS.tertiary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.tertiary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_one"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    INTAKE_NOTE_MED_LABELS.additional_diagnosis_one
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_one?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_two"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    INTAKE_NOTE_MED_LABELS.additional_diagnosis_two
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_two?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <MedNoteEPrescribe
                    noteDetails={noteDetails}
                    showDiagnosisSync
                />
                <h3 className="note-header">Initial Diagnostic Interview</h3>
                <h3 className="note-header">Identities</h3>
                <div className="fg">
                    <Input
                        {...register("preferred_name")}
                        label={INTAKE_NOTE_MED_LABELS.preferred_name}
                        defaultValue={noteDetails.note_content?.preferred_name}
                        hasError={!!errors.preferred_name}
                        errorText={errors.preferred_name?.message}
                        isRequired
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="pronoun"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isRequired
                                label={INTAKE_NOTE_MED_LABELS.pronoun}
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    pronounOptionsForSelect.find(
                                        (option) => option.value === field.value
                                    ) || null
                                }
                                options={pronounOptionsForSelect}
                                hasError={!!errors.pronoun}
                                errorText={errors.pronoun?.message}
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="sex"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isOptional
                                label={INTAKE_NOTE_MED_LABELS.sex}
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    sexOptionsForSelect.find(
                                        (option) => option.value === field.value
                                    ) || null
                                }
                                options={sexOptionsForSelect}
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="gender_identity"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isOptional
                                label={INTAKE_NOTE_MED_LABELS.gender_identity}
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    genderIdentityOptionsForSelect.find(
                                        (option) => option.value === field.value
                                    ) || null
                                }
                                options={genderIdentityOptionsForSelect}
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="sexual_orientation"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isOptional
                                label={
                                    INTAKE_NOTE_MED_LABELS.sexual_orientation
                                }
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    sexualOrientationOptionsForSelect.find(
                                        (option) => option.value === field.value
                                    ) || null
                                }
                                options={sexualOrientationOptionsForSelect}
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Input
                        {...register("monogamous_polyamorous")}
                        label={INTAKE_NOTE_MED_LABELS.monogamous_polyamorous}
                        defaultValue={
                            noteDetails.note_content?.monogamous_polyamorous
                        }
                        isOptional
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="religion"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isOptional
                                label={INTAKE_NOTE_MED_LABELS.religion}
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    religionOptionsForSelect.find(
                                        (option) => option.value === field.value
                                    ) || null
                                }
                                options={religionOptionsForSelect}
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Input
                        {...register("religion_details")}
                        label={INTAKE_NOTE_MED_LABELS.religion_details}
                        defaultValue={
                            noteDetails.note_content?.religion_details
                        }
                        isOptional
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="race"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isOptional
                                label={INTAKE_NOTE_MED_LABELS.race}
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    raceOptionsForSelect.find(
                                        (option) => option.value === field.value
                                    ) || null
                                }
                                options={raceOptionsForSelect}
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Input
                        {...register("ethnicity")}
                        label={INTAKE_NOTE_MED_LABELS.ethnicity}
                        defaultValue={noteDetails.note_content?.ethnicity}
                        isOptional
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="education"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isOptional
                                label={INTAKE_NOTE_MED_LABELS.education}
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    educationOptionsForSelect.find(
                                        (option) => option.value === field.value
                                    ) || null
                                }
                                options={educationOptionsForSelect}
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("current_employment")}
                        label={INTAKE_NOTE_MED_LABELS.current_employment}
                        defaultValue={
                            noteDetails.note_content?.current_employment
                        }
                        isOptional
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="relationship"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isOptional
                                label={INTAKE_NOTE_MED_LABELS.relationship}
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    relationshipOptionsForSelect.find(
                                        (option) => option.value === field.value
                                    ) || null
                                }
                                options={relationshipOptionsForSelect}
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Input
                        {...register("relationship_details")}
                        label={INTAKE_NOTE_MED_LABELS.relationship_details}
                        defaultValue={
                            noteDetails.note_content?.relationship_details
                        }
                        isOptional
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("emergency_contact_information")}
                        label={
                            INTAKE_NOTE_MED_LABELS.emergency_contact_information
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.emergency_contact_information
                        }
                        hasError={!!errors.emergency_contact_information}
                        errorText={
                            errors.emergency_contact_information?.message
                        }
                        isRequired
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register(
                            "names_and_relations_of_others_present_in_the_interview"
                        )}
                        label={
                            INTAKE_NOTE_MED_LABELS.names_and_relations_of_others_present_in_the_interview
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.names_and_relations_of_others_present_in_the_interview
                        }
                        isOptional
                    />
                </div>
                <h3 className="note-header">Presenting Concern and History</h3>
                <div className="fg">
                    <TextArea
                        {...register("chief_complaint")}
                        label={INTAKE_NOTE_MED_LABELS.chief_complaint}
                        defaultValue={noteDetails.note_content?.chief_complaint}
                        hasError={!!errors.chief_complaint}
                        errorText={errors.chief_complaint?.message}
                        isRequired
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("history_of_present_illness")}
                        label={
                            INTAKE_NOTE_MED_LABELS.history_of_present_illness
                        }
                        defaultValue={
                            noteDetails.note_content?.history_of_present_illness
                        }
                        hasError={!!errors.history_of_present_illness}
                        errorText={errors.history_of_present_illness?.message}
                        isRequired
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("past_psychiatric_history")}
                        label={INTAKE_NOTE_MED_LABELS.past_psychiatric_history}
                        defaultValue={
                            noteDetails.note_content?.past_psychiatric_history
                        }
                        hasError={!!errors.past_psychiatric_history}
                        errorText={errors.past_psychiatric_history?.message}
                        isRequired
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("past_medical_history")}
                        label={INTAKE_NOTE_MED_LABELS.past_medical_history}
                        defaultValue={
                            noteDetails.note_content?.past_medical_history
                        }
                        hasError={!!errors.past_medical_history}
                        errorText={errors.past_medical_history?.message}
                        isRequired
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("past_psychiatric_hospitalization")}
                        label={
                            INTAKE_NOTE_MED_LABELS.past_psychiatric_hospitalization
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.past_psychiatric_hospitalization
                        }
                        hasError={!!errors.past_psychiatric_hospitalization}
                        errorText={
                            errors.past_psychiatric_hospitalization?.message
                        }
                        isRequired
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("current_medications")}
                        label={INTAKE_NOTE_MED_LABELS.current_medications}
                        defaultValue={
                            noteDetails.note_content?.current_medications
                        }
                        hasError={!!errors.current_medications}
                        errorText={errors.current_medications?.message}
                        isRequired
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("allergies")}
                        label={INTAKE_NOTE_MED_LABELS.allergies}
                        defaultValue={noteDetails.note_content?.allergies}
                        hasError={!!errors.allergies}
                        errorText={errors.allergies?.message}
                        isRequired
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("social_history")}
                        label={INTAKE_NOTE_MED_LABELS.social_history}
                        defaultValue={noteDetails.note_content?.social_history}
                        hasError={!!errors.social_history}
                        errorText={errors.social_history?.message}
                        isRequired
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("family_history")}
                        label={INTAKE_NOTE_MED_LABELS.family_history}
                        defaultValue={noteDetails.note_content?.family_history}
                        hasError={!!errors.family_history}
                        errorText={errors.family_history?.message}
                        isRequired
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("trauma_history")}
                        label={INTAKE_NOTE_MED_LABELS.trauma_history}
                        defaultValue={noteDetails.note_content?.trauma_history}
                        hasError={!!errors.trauma_history}
                        errorText={errors.trauma_history?.message}
                        isRequired
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("lifestyle_excercise_diet_sleep")}
                        label={
                            INTAKE_NOTE_MED_LABELS.lifestyle_excercise_diet_sleep
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.lifestyle_excercise_diet_sleep
                        }
                        hasError={!!errors.lifestyle_excercise_diet_sleep}
                        errorText={
                            errors.lifestyle_excercise_diet_sleep?.message
                        }
                        isRequired
                    />
                </div>
                <h3 className="note-header">Mental Status Exam</h3>
                <div className="fg fg-space-between two flex">
                    <TextArea
                        {...register("appearance")}
                        label={INTAKE_NOTE_MED_LABELS.appearance}
                        defaultValue={noteDetails.note_content?.appearance}
                        isRequired
                        hasError={!!errors.appearance}
                        errorText={errors.appearance?.message}
                    />
                    <TextArea
                        {...register("movement")}
                        label={INTAKE_NOTE_MED_LABELS.movement}
                        defaultValue={noteDetails.note_content?.movement}
                        isRequired
                        hasError={!!errors.movement}
                        errorText={errors.movement?.message}
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("speech_orientation")}
                        label={INTAKE_NOTE_MED_LABELS.speech_orientation}
                        defaultValue={
                            noteDetails.note_content?.speech_orientation
                        }
                        isRequired
                        hasError={!!errors.speech_orientation}
                        errorText={errors.speech_orientation?.message}
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("affect_mood")}
                        label={INTAKE_NOTE_MED_LABELS.affect_mood}
                        defaultValue={noteDetails.note_content?.affect_mood}
                        isRequired
                        hasError={!!errors.affect_mood}
                        errorText={errors.affect_mood?.message}
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("attention_concentration_cognition")}
                        label={
                            INTAKE_NOTE_MED_LABELS.attention_concentration_cognition
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.attention_concentration_cognition
                        }
                        isRequired
                        hasError={!!errors.attention_concentration_cognition}
                        errorText={
                            errors.attention_concentration_cognition?.message
                        }
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("overall_presentation")}
                        label={INTAKE_NOTE_MED_LABELS.overall_presentation}
                        defaultValue={
                            noteDetails.note_content?.overall_presentation
                        }
                        isRequired
                        hasError={!!errors.overall_presentation}
                        errorText={errors.overall_presentation?.message}
                    />
                </div>
                <h3 className="note-label-req">
                    {
                        INTAKE_NOTE_MED_LABELS.patient_ability_and_capability_to_respond_to_treatment
                    }
                </h3>
                <div className="note-checkboxes">
                    {PATIENT_ABILITY_OPTIONS.map((value) => (
                        <FormCheckbox
                            key={value}
                            {...register(
                                "patient_ability_and_capability_to_respond_to_treatment"
                            )}
                            label={value}
                            value={value}
                            defaultChecked={handleIsChecked(
                                value,
                                noteDetails.note_content
                                    ?.patient_ability_and_capability_to_respond_to_treatment
                            )}
                        />
                    ))}
                </div>
                {errors.patient_ability_and_capability_to_respond_to_treatment ? (
                    <div className="note-error">
                        <p>Please select at least one option</p>
                    </div>
                ) : null}
                <div className="fg">
                    <TextArea
                        {...register(
                            "diminished_poor_or_limited_quality_patient_oriented_evidence_details"
                        )}
                        label={
                            INTAKE_NOTE_MED_LABELS.diminished_poor_or_limited_quality_patient_oriented_evidence_details
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.diminished_poor_or_limited_quality_patient_oriented_evidence_details
                        }
                        isRequired={requiresPatientAbilityDetails}
                        isOptional={!requiresPatientAbilityDetails}
                        hasError={
                            !!errors.diminished_poor_or_limited_quality_patient_oriented_evidence_details
                        }
                        errorText={
                            errors
                                .diminished_poor_or_limited_quality_patient_oriented_evidence_details
                                ?.message
                        }
                    />
                </div>
                <h3 className="note-header">Review of Systems</h3>
                <div className="fg">
                    <TextArea
                        {...register("ros")}
                        label={INTAKE_NOTE_MED_LABELS.ros}
                        defaultValue={noteDetails.note_content?.ros}
                        hasError={!!errors.ros}
                        errorText={errors.ros?.message}
                        isRequired
                    />
                </div>
                <h3 className="note-header">Risk Assessment</h3>
                {RISK_ASSESSMENT_FIELDS.map(({ name, options }) => (
                    <div key={name} className="fg">
                        <h3 className="note-label-req">
                            {INTAKE_NOTE_MED_LABELS[name]}
                        </h3>
                        <div className="note-radios">
                            {options.map((value) => (
                                <RadioInput
                                    key={value}
                                    {...register(name)}
                                    label={value}
                                    value={value}
                                    defaultChecked={
                                        noteDetails.note_content?.[name] ===
                                        value
                                    }
                                />
                            ))}
                        </div>
                        {errors[name] ? (
                            <div className="note-error">
                                <p>{errors[name]?.message}</p>
                            </div>
                        ) : null}
                    </div>
                ))}
                <div className="fg">
                    <TextArea
                        {...register("other_pertinent_information")}
                        label={
                            INTAKE_NOTE_MED_LABELS.other_pertinent_information
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.other_pertinent_information
                        }
                        isOptional
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("lab_test_data_review")}
                        label={INTAKE_NOTE_MED_LABELS.lab_test_data_review}
                        defaultValue={
                            noteDetails.note_content?.lab_test_data_review
                        }
                        hasError={!!errors.lab_test_data_review}
                        errorText={errors.lab_test_data_review?.message}
                        isOptional
                    />
                </div>
                <div className="fg">
                    <ScreeningTools
                        onComplete={(result) => {
                            const currentResults = getValues(
                                "psychiatric_screening_measure_results"
                            );

                            setValue(
                                "psychiatric_screening_measure_results",
                                [currentResults?.trim(), result]
                                    .filter(Boolean)
                                    .join("\n\n"),
                                {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                }
                            );
                        }}
                    />
                    <TextArea
                        {...register("psychiatric_screening_measure_results")}
                        label={
                            INTAKE_NOTE_MED_LABELS.psychiatric_screening_measure_results
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.psychiatric_screening_measure_results
                        }
                        hasError={
                            !!errors.psychiatric_screening_measure_results
                        }
                        errorText={
                            errors.psychiatric_screening_measure_results
                                ?.message
                        }
                        isRequired
                    />
                </div>
                <h3 className="note-header">
                    Goals of Care and Treatment Plan
                </h3>
                <div className="fg">
                    <TextArea
                        {...register("note")}
                        label={INTAKE_NOTE_MED_LABELS.note}
                        defaultValue={noteDetails.note_content?.note}
                        hasError={!!errors.note}
                        errorText={errors.note?.message}
                        isRequired
                    />
                </div>
                <h3 className="note-label-req">
                    {INTAKE_NOTE_MED_LABELS.plan}
                </h3>
                <p className="note-label">
                    Pharmacologic interventions: including dosage, route, and
                    frequency and non-pharmacologic
                </p>
                <div className="fg">
                    <TextArea
                        {...register("plan")}
                        defaultValue={noteDetails.note_content?.plan}
                        hasError={!!errors.plan}
                        errorText={errors.plan?.message}
                        isRequired
                    />
                </div>
                <h3 className="note-label-req">
                    {INTAKE_NOTE_MED_LABELS.frequency_of_sessions}
                </h3>
                <div className="note-checkboxes">
                    {FREQUENCY_OF_SESSIONS_OPTIONS.map((value) => (
                        <FormCheckbox
                            key={value}
                            {...register("frequency_of_sessions")}
                            label={value}
                            value={value}
                            defaultChecked={handleIsChecked(
                                value,
                                noteDetails.note_content?.frequency_of_sessions
                            )}
                        />
                    ))}
                </div>
                {errors.frequency_of_sessions ? (
                    <div className="note-error">
                        <p>{errors.frequency_of_sessions.message}</p>
                    </div>
                ) : null}
                <div className="fg">
                    <Input
                        {...register("other_frequency_of_sessions")}
                        label={
                            INTAKE_NOTE_MED_LABELS.other_frequency_of_sessions
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.other_frequency_of_sessions
                        }
                        isOptional
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="date_of_next_session"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                isRequired
                                label={
                                    INTAKE_NOTE_MED_LABELS.date_of_next_session
                                }
                                onChange={(date) => {
                                    field.onChange(date ?? null);
                                    setLocalDateOfNextSession(date);
                                }}
                                value={
                                    field.value
                                        ? formatDate(field.value, true)
                                        : undefined
                                }
                                selected={localDateOfNextSession}
                                hasError={Boolean(errors.date_of_next_session)}
                                errorText={errors.date_of_next_session?.message}
                            />
                        )}
                    />
                </div>
                <h3 className="note-label-req">
                    {INTAKE_NOTE_MED_LABELS.prognosis}
                </h3>
                <p className="note-label">
                    Please write at least 1-2 sentences about the likely
                    outcome/development of the symptoms/challenges
                </p>
                <div className="fg">
                    <TextArea
                        {...register("prognosis")}
                        defaultValue={noteDetails.note_content?.prognosis}
                        hasError={!!errors.prognosis}
                        errorText={errors.prognosis?.message}
                        isRequired
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register(
                            "education_including_health_promotion_maintenance_and_psychosocial_needs"
                        )}
                        label={
                            INTAKE_NOTE_MED_LABELS.education_including_health_promotion_maintenance_and_psychosocial_needs
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.education_including_health_promotion_maintenance_and_psychosocial_needs
                        }
                        hasError={
                            !!errors.education_including_health_promotion_maintenance_and_psychosocial_needs
                        }
                        errorText={
                            errors
                                .education_including_health_promotion_maintenance_and_psychosocial_needs
                                ?.message
                        }
                        isRequired
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("lab_work_ordered")}
                        label={INTAKE_NOTE_MED_LABELS.lab_work_ordered}
                        defaultValue={
                            noteDetails.note_content?.lab_work_ordered
                        }
                        isOptional
                    />
                </div>
                <h3 className="note-label-req">
                    {INTAKE_NOTE_MED_LABELS.referrals_and_recommendations}
                </h3>
                <p className="note-label">
                    Referrals to psychiatric and medical providers, therapy
                    recommendations, holistic options and complementary
                    therapies and rationale for decision
                </p>
                <div className="fg">
                    <TextArea
                        {...register("referrals_and_recommendations")}
                        defaultValue={
                            noteDetails.note_content
                                ?.referrals_and_recommendations
                        }
                        hasError={!!errors.referrals_and_recommendations}
                        errorText={
                            errors.referrals_and_recommendations?.message
                        }
                        isRequired
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register(
                            "overall_goals_of_care_frequency_and_duration_of_visits"
                        )}
                        label={
                            INTAKE_NOTE_MED_LABELS.overall_goals_of_care_frequency_and_duration_of_visits
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.overall_goals_of_care_frequency_and_duration_of_visits
                        }
                        hasError={
                            !!errors.overall_goals_of_care_frequency_and_duration_of_visits
                        }
                        errorText={
                            errors
                                .overall_goals_of_care_frequency_and_duration_of_visits
                                ?.message
                        }
                        isRequired
                    />
                </div>
                <h3 className="note-header">Treatment Goals</h3>
                <div className="fg">
                    <TextArea
                        {...register("treatment_goal_one")}
                        label={INTAKE_NOTE_MED_LABELS.treatment_goal_one}
                        defaultValue={
                            noteDetails.note_content?.treatment_goal_one
                        }
                        hasError={!!errors.treatment_goal_one}
                        errorText={errors.treatment_goal_one?.message}
                        isRequired
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("treatment_goal_two")}
                        label={INTAKE_NOTE_MED_LABELS.treatment_goal_two}
                        defaultValue={
                            noteDetails.note_content?.treatment_goal_two
                        }
                        hasError={!!errors.treatment_goal_two}
                        errorText={errors.treatment_goal_two?.message}
                        isRequired
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("treatment_goal_three")}
                        label={INTAKE_NOTE_MED_LABELS.treatment_goal_three}
                        defaultValue={
                            noteDetails.note_content?.treatment_goal_three
                        }
                        hasError={!!errors.treatment_goal_three}
                        errorText={errors.treatment_goal_three?.message}
                        isRequired
                    />
                </div>

                <div className="fg">
                    <Controller
                        name="modality"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isRequired
                                label={INTAKE_NOTE_MED_LABELS.modality}
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    modalityOptionsForSelect.find(
                                        (option) => option.value === field.value
                                    ) || null
                                }
                                options={modalityOptionsForSelect}
                                hasError={!!errors.modality}
                                errorText={errors.modality?.message}
                            />
                        )}
                    />
                </div>

                <h3 className="note-label note-label-opt">
                    {
                        INTAKE_NOTE_MED_LABELS.communication_factors_impacting_treatment
                    }
                </h3>
                <div className="note-checkboxes">
                    {COMMUNICATION_FACTORS_OPTIONS.map((value) => (
                        <FormCheckbox
                            key={value}
                            {...register(
                                "communication_factors_impacting_treatment"
                            )}
                            label={value}
                            value={value}
                            defaultChecked={handleIsChecked(
                                value,
                                noteDetails.note_content
                                    ?.communication_factors_impacting_treatment
                            )}
                        />
                    ))}
                </div>
                <div className="fg">
                    <TextArea
                        {...register(
                            "details_around_communication_factors_impacting_treatment"
                        )}
                        label={
                            INTAKE_NOTE_MED_LABELS.details_around_communication_factors_impacting_treatment
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.details_around_communication_factors_impacting_treatment
                        }
                        isOptional
                    />
                </div>
                <h3 className="note-label-opt">
                    {INTAKE_NOTE_MED_LABELS.files}
                </h3>
                <div className="fg flex flex-col gap-y-[--spacing-form-items]">
                    <FilePicker.FilePicker
                        onDrop={onDrop}
                        disabled={isAddDocMutating > 0}
                    >
                        <FilePicker.FilePickerIcon />
                        <FilePicker.FilePickerMeta description="Click to upload note files" />
                    </FilePicker.FilePicker>
                    {selectedFiles && selectedFiles.length > 0 ? (
                        <div className="flex flex-col gap-y-[--spacing-items]">
                            <FilePicker.FilePickerSelectedFiles
                                selectedFiles={selectedFiles}
                                handleRemoveSelectedFile={
                                    handleRemoveSelectedFile
                                }
                                wrapperClassName="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] grid-flow-row flex-row gap-[--spacing-items]"
                            />
                            <Button
                                className="self-start"
                                onClick={() =>
                                    handleAddNoteDocument(selectedFiles, () =>
                                        setSelectedFiles(null)
                                    )
                                }
                                disabled={isAddDocMutating > 0}
                            >
                                Save files
                            </Button>
                            {uploadProgress > 0 && uploadProgress < 100 && (
                                <Progress.Root value={uploadProgress}>
                                    <Progress.Label>
                                        Uploading {selectedFiles.length}{" "}
                                        {selectedFiles.length === 1
                                            ? "file"
                                            : "files"}
                                        ...
                                    </Progress.Label>
                                    <Progress.Value />
                                    <Progress.Track>
                                        <Progress.Indicator />
                                    </Progress.Track>
                                </Progress.Root>
                            )}
                        </div>
                    ) : null}
                    {noteDetails?.note_content_documents &&
                    noteDetails?.note_content_documents.length > 0 ? (
                        <div className="flex flex-col gap-y-[var(--spacing-items)]">
                            {noteDetails.note_content_documents.map((doc) => (
                                <div
                                    key={doc.note_content_document_id}
                                    className="flex items-center gap-x-3"
                                >
                                    <ActionButton
                                        variant="can-hover"
                                        size="auto"
                                        className="h-7 !text-text text-caption underline"
                                        actionTitle="View file"
                                        onClick={() => {
                                            setDocInView(doc);
                                        }}
                                    >
                                        <span>{`${displayNameInRightFormat(
                                            {
                                                firstName:
                                                    doc.uploaded_by.first_name,
                                                lastName:
                                                    doc.uploaded_by.last_name,
                                            },
                                            false
                                        )} -  ${doc.document_name}`}</span>
                                    </ActionButton>
                                    <ActionButton
                                        variant="can-hover"
                                        aria-label="delete icon"
                                        size="auto"
                                        className="h-7"
                                        onClick={() =>
                                            handleDeleteNoteContentDocument(
                                                doc.note_content_document_id
                                            )
                                        }
                                        disabled={isDeleteDocMutating > 0}
                                        actionTitle="Delete file from note content"
                                    >
                                        <DeleteIcon />
                                    </ActionButton>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
                <div className="fg-info fg-line">
                    <p>Sign note here</p>
                    <div className="fg">
                        <Input
                            {...register("signature")}
                            hasError={Boolean(errors.signature)}
                            errorText={errors.signature?.message}
                            label="Provider's Initials"
                            autoComplete="off"
                            isRequired
                        />
                    </div>
                </div>
                <div>
                    <EditNoteFooter
                        onSaveButtonClick={() => {
                            isSigning.current = false;
                            handleSubmit(handleEditDraftNote)();
                        }}
                        noteDOS={noteDetails.date_of_service}
                        onSubmitButtonClick={() => {
                            isSigning.current = true;
                        }}
                    />
                </div>
            </form>
        </>
    );
}
